// src/utils/fetch.ts
import { refreshTokenApi } from '../features/system/auth/api/authApi';
import { fmt } from './date';
import { notifyAuthDisconnected } from '@/lib/auth/authDisconnect';

/* ── JWT helpers (unchanged) ────────────────────────────────── */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const [, payload] = token.split('.');
    const json = atob(
      payload.replace(/-/g, '+').replace(/_/g, '/')
        .padEnd(payload.length + (4 - (payload.length % 4)) % 4, '='),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}
const isTokenExpired = (t: string) =>
  !decodeJwtPayload(t)?.exp || Date.now() / 1000 > decodeJwtPayload(t)!.exp!;

/* ── Types ──────────────────────────────────────────────────── */
export interface FetchOptions extends RequestInit {
  /** If true, do NOT add / refresh JWTs */
  skipAuth?: boolean;
}

/* ── ISO-timestamp pretty-printer (unchanged) ───────────────── */
function formatTimestamps(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(obj)) {
      return fmt(obj);
    }
    return obj;
  }
  if (Array.isArray(obj)) return obj.map(formatTimestamps);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, formatTimestamps(v)]),
  );
}

/* ── Internal helpers ───────────────────────────────────────── */
/**
 * Return a non-expired access JWT when local token material exists.
 *
 * Contract (single responsibility):
 *   • If a valid (non-expired) access token exists → return it.
 *     Refresh token is NOT required in this path — many auth modes
 *     (device flow, server-set short-lived tokens, future SSO) issue
 *     access tokens without persisting a refresh token on the client.
 *   • If the access token is expired and a refresh token exists → refresh.
 *   • If no local auth material exists → return null and let the request run.
 *     This supports cookie-backed/session-backed auth modes.
 */
async function ensureFreshJwt(): Promise<string | null> {
  const access = localStorage.getItem('jwt');
  if (access && !isTokenExpired(access)) return access;

  const refresh = localStorage.getItem('refreshToken');
  if (!refresh) return null;

  const { access_token: fresh } = await refreshTokenApi();
  localStorage.setItem('jwt', fresh);
  return fresh;
}

function withJwtHeaders(init: FetchOptions, jwt: string): FetchOptions {
  return {
    ...init,
    headers: {
      ...(init.headers as Record<string, string>),
      Authorization: `Bearer ${jwt}`, // local/dev
      'x-app-jwt': jwt,               // Cloud Run
    },
  };
}

/* ── Main helper ────────────────────────────────────────────── */
export async function fetchData(
  path: string,
  options: FetchOptions = {},
): Promise<any> {
  const method = (options.method || 'GET').toUpperCase();

  /*── 1. attach / refresh JWT if needed ──────────────────────*/
  let init: FetchOptions = { ...options };
  if (!options.skipAuth) {
    const jwt = await ensureFreshJwt();
    if (jwt) init = withJwtHeaders(init, jwt);
  }

  console.log(`👉 [fetchData] ${method} ${path}`, init);

  /*── 2. make the request (with one automatic 401 retry) ─────*/
  const doFetch = () => fetch(path, init).catch(err => {
    console.error(`❌ [fetchData] Network error for ${method} ${path}:`, err);
    throw new Error(`Network error: ${(err as Error).message}`);
  });

  let response = await doFetch();
  console.log(`👈 [fetchData] ${response.status} ${response.statusText} for ${path}`);

  if (response.status === 401 && !options.skipAuth) {
    // possible token revocation / missing token bootstrap → try once more
    const freshJwt = await ensureFreshJwt();
    if (freshJwt) {
      init = withJwtHeaders(init, freshJwt);
      response = await doFetch();
      console.log(`👈 [fetchData] Retry → ${response.status} ${response.statusText}`);
    }
  }

  // If we still ended up unauthorized, trigger the global auth-drop flow.
  // 403 (forbidden) means "you're logged in but lack this permission" — NOT a session issue.
  // Only 401 should force re-login.
  if (!options.skipAuth && response.status === 401) {
    notifyAuthDisconnected({
      reason: 'unauthenticated',
      status: response.status,
      url: path,
    });
  }

  /*── 3. error handling ──────────────────────────────────────*/
  if (!response.ok) {
    const body = await response.text().catch(() => '<unreadable>');
    console.error(`❌ [fetchData] ${response.status} ${response.statusText} (${method} ${path})`);
    console.error('Body:', body);

    // Parse the BFF's structured error shape when available
    let parsed: { error?: string; fault?: string; message?: string; detail?: string } | null = null;
    try { parsed = JSON.parse(body); } catch { /* not JSON */ }

    const err = new Error(
      parsed?.detail || parsed?.message || `Fetch failed: ${response.status} ${response.statusText}`,
    ) as Error & { status: number; fault: string; errorCode: string; detail: string };
    err.status    = response.status;
    err.fault     = parsed?.fault || (response.status >= 500 ? 'bff' : 'upstream');
    err.errorCode = parsed?.error || 'unknown';
    err.detail    = parsed?.detail || body;
    throw err;
  }

  /*── 4. happy-path parsing ─────────────────────────────────*/
  // 204 / 205 → No Content
  if (response.status === 204 || response.status === 205) return;

  // Only parse JSON when the server says so
  const ctype = response.headers.get('content-type') ?? '';
  if (!ctype.startsWith('application/json')) return response.blob(); // caller decides

  let data: any;
  try {
    data = await response.json();
  } catch (e) {
    console.error(`❌ [fetchData] JSON parse error for ${path}:`, e);
    throw new Error(`Invalid JSON response: ${(e as Error).message}`);
  }

  console.log(`✅ [fetchData] Parsed JSON from ${path}:`, data);
  return formatTimestamps(data);
}
